import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Upravlja položajem skrola pri navigaciji.
 *
 *  • bez hash-a  → vrh stranice
 *  • sa hash-om  → na ciljani element
 *
 * Ranije je bezuslovno skrolovao na vrh i pratio samo `pathname`, pa nijedan
 * link sa sidrom nije radio: `/usluge#web` bi sleteo na vrh stranice, a
 * prelazak sa `#web` na `#mobilne` ne bi pomerio ništa jer se putanja ne menja.
 *
 * Cilj se traži u više pokušaja: stranice se učitavaju lenjo, pa element sa
 * tim id-em najčešće još ne postoji u trenutku kad se efekat izvrši.
 */

const MAX_FRAMES = 60; // ~1s pri 60fps — dovoljno da lazy stranica stigne

export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      return undefined;
    }

    let frame = 0;
    let raf = 0;

    const tryScroll = () => {
      // decodeURIComponent: sidra sa dijakritikom stižu kodirana iz adrese
      let id = hash.slice(1);
      try { id = decodeURIComponent(id); } catch { /* ostavi kako jeste */ }

      const target = document.getElementById(id);
      if (target) {
        // scroll-margin-top na cilju drži element ispod lepljivog navbara
        target.scrollIntoView({ behavior: reduced ? 'instant' : 'smooth', block: 'start' });
        return;
      }

      if (++frame < MAX_FRAMES) {
        raf = requestAnimationFrame(tryScroll);
      } else {
        // Sidro ne postoji (npr. zastareo link) — bar nemoj ostaviti
        // posetioca na sredini prethodne stranice.
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      }
    };

    raf = requestAnimationFrame(tryScroll);
    return () => cancelAnimationFrame(raf);
  }, [pathname, hash]);

  return null;
}
